import { useCallback, useState } from "react";
import Env from "@/core/config/env";
import { getRequestErrorMessage } from "@/shared/utils/getRequestErrorMessage";
import { useCreatePortalSession } from "./useCreatePortalSession";

/** Copy da falha ao abrir a troca de plano (aba Faturamento e bloqueio). */
export const PLAN_CHANGE_ERROR_MESSAGE =
  "Não foi possível abrir a troca de plano. Tente novamente.";

/**
 * Ação de troca de plano, a mesma no bloco de uso da aba Faturamento e no
 * bloqueio por plano (SA-1401):
 * - `openPlanChange`: Portal do Stripe na troca de plano
 *   (`subscription_update`). O servidor recusa (409) o plano legado, que no
 *   Portal default veria plano "maior" mais barato do que paga; a mensagem
 *   dele chega em `planChangeError`.
 * - `openSales`: WhatsApp de Vendas, para quem não troca sozinho (conta sem
 *   assinatura no Stripe, plano legado, topo da escada).
 *
 * Quem chama decide o caminho e registra o próprio evento no PostHog.
 */
export function usePlanChangeActions(companyId: string | null | undefined) {
  const portalSession = useCreatePortalSession(companyId);
  const [planChangeError, setPlanChangeError] = useState<string | null>(null);

  const openPlanChange = async () => {
    setPlanChangeError(null);
    try {
      const { url } = await portalSession.mutateAsync("subscription_update");
      window.location.href = url;
    } catch (error) {
      setPlanChangeError(
        getRequestErrorMessage(error, PLAN_CHANGE_ERROR_MESSAGE)
      );
    }
  };

  // Estável: quem memoiza sobre ela (o aviso de trial) não recalcula a cada
  // render.
  const openSales = useCallback((message: string) => {
    const url = `${Env.VITE_SUPPORT_WHATSAPP_URL}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  return {
    openPlanChange,
    openSales,
    isOpeningPlanChange: portalSession.isPending,
    planChangeError,
  };
}
